import {
  GameAttributes,
  GameAttributeConfig,
  GameAttributeSection,
  DASH,
} from '@/constants/GameAttributes';
import { GameStatuses } from '@/constants/GameStatuses';
import Languages from '@/constants/Languages';
import Markets from '@/constants/Markets';
import { fetchFilters } from '@/lib/api';
import { GameSchema } from '@/lib/types';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import StarIcon from '@/assets/star.svg?react';
import AttributeBadge from './AttributeBadge';

const getSectionAttributes = (
  game: GameSchema | undefined,
  section: GameAttributeSection,
) =>
  Object.entries(GameAttributes)
    .filter(([, attribute]) => attribute.section === section)
    .map(([key, attribute]) => ({
      key,
      ...attribute,
      value: game ? attribute.parseValue(game) : null,
    }));

const renderAttributeValue = (
  attribute: Pick<GameAttributeConfig, 'variant'>,
  value: string | boolean | null | undefined,
) => {
  if (value === null || value === undefined) return DASH;

  switch (attribute.variant) {
    case 'badgeSupport':
      return (
        <AttributeBadge positive={!!value}>
          {value ? (
            <FormattedMessage
              id="supported"
              defaultMessage="Supported"
            />
          ) : (
            <FormattedMessage
              id="notSupported"
              defaultMessage="Not Supported"
            />
          )}
        </AttributeBadge>
      );
    case 'badgeYesNo':
      return (
        <AttributeBadge positive={!!value}>
          {value ? (
            <FormattedMessage id="yes" defaultMessage="Yes" />
          ) : (
            <FormattedMessage id="no" defaultMessage="No" />
          )}
        </AttributeBadge>
      );
    case 'badgeStatus': {
      const statusKey = String(value).toLowerCase();
      return (
        <AttributeBadge positive={statusKey === 'released'}>
          {GameStatuses[statusKey as keyof typeof GameStatuses]?.label ||
            value}
        </AttributeBadge>
      );
    }
    default:
      return value;
  }
};

const GameDetails = ({ game }: { game?: GameSchema }) => {
  const overviewAttributes = getSectionAttributes(game, 'overview');
  const gameSetupAttributes = getSectionAttributes(game, 'gameSetup');
  const mathInformationAttributes = getSectionAttributes(
    game,
    'mathInformation',
  );
  const featuresSupportAttributes = getSectionAttributes(
    game,
    'featuresSupport',
  );

  const { data } = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
  });
  const gameMarkets = data?.markets?.filter((market) =>
    game?.market_ids.includes(market.market_id),
  );
  const marketItems =
    gameMarkets?.map((market) => ({
      key: market.market_id,
      ...(Markets?.[market.market_name] || {
        label: market.market_name,
      }),
    })) || [];
  const languageItems =
    game?.languages?.map((language) => ({
      key: language,
      ...(Languages?.[language.toLowerCase()] || {
        label: language,
      }),
    })) || [];
  const featureItems =
    game?.game_features?.map((feature) => ({
      key: feature,
      label: feature,
    })) || [];

  return (
    <Root>
      <MainColumn>
        <MainTitle>
          <FormattedMessage
            id="detailedGameInformation"
            defaultMessage="Detailed Game Information"
          />
        </MainTitle>

        <Section>
          <SectionHeader>
            <Bar />
            <FormattedMessage id="overview" defaultMessage="Overview" />
          </SectionHeader>
          <OverviewGrid>
            {overviewAttributes.map(({ key, value, label, icon, variant }) => (
              <Tile key={key}>
                <TileLabel>
                  {icon}
                  {label}
                </TileLabel>
                <TileValue>
                  {renderAttributeValue({ variant }, value)}
                </TileValue>
              </Tile>
            ))}
          </OverviewGrid>
        </Section>

        <TwoColumnSection>
          <Column>
            <SectionHeader>
              <Bar />
              <FormattedMessage
                id="gameSetup"
                defaultMessage="Game Setup"
              />
            </SectionHeader>
            {gameSetupAttributes.map(({ key, value, label, variant }) => (
              <InfoRow key={key}>
                <InfoLabel>{label}</InfoLabel>
                <InfoValue>
                  {renderAttributeValue({ variant }, value)}
                </InfoValue>
              </InfoRow>
            ))}
          </Column>
          <ColumnDivider />
          <Column>
            <SectionHeader>
              <Bar />
              <FormattedMessage
                id="mathInformation"
                defaultMessage="Math Information"
              />
            </SectionHeader>
            {mathInformationAttributes.map(
              ({ key, value, label, variant }) => (
                <InfoRow key={key}>
                  <InfoLabel>{label}</InfoLabel>
                  <InfoValue>
                    {renderAttributeValue({ variant }, value)}
                  </InfoValue>
                </InfoRow>
              ),
            )}
          </Column>
        </TwoColumnSection>

        <Section>
          <SectionHeader>
            <StarIcon />
            <FormattedMessage
              id="featuresAndSupport"
              defaultMessage="Features & Support"
            />
          </SectionHeader>
          <FeaturesGrid>
            {featuresSupportAttributes.map(
              ({ key, value, label, variant }) => (
                <FeatureCell key={key}>
                  <InfoLabel>{label}</InfoLabel>
                  {renderAttributeValue({ variant }, value)}
                </FeatureCell>
              ),
            )}
            <FeatureCell>
              <FeatureCellColumn>
                <InfoLabel>
                  <FormattedMessage
                    id="gameFeatures"
                    defaultMessage="Game Features"
                  />
                  {': '}
                  {featureItems.length}
                </InfoLabel>
                <FeatureList>
                  {featureItems.length
                    ? featureItems.map(({ label }) => label).join(' · ')
                    : DASH}
                </FeatureList>
              </FeatureCellColumn>
            </FeatureCell>
          </FeaturesGrid>
        </Section>
      </MainColumn>
      <DividerContainer>
        <Divider />
        <div>
          <Title>
            <FormattedMessage
              id="supportedMarkets"
              defaultMessage="Supported Markets"
            />
            {': '}
            {marketItems.length}
          </Title>
          {marketItems.map(({ key, icon, label }) => (
            <Row key={key}>
              {icon}
              <Value>{label}</Value>
            </Row>
          ))}
          <Title>
            <FormattedMessage
              id="supportedLanguages"
              defaultMessage="Supported Languages"
            />
            {': '}
            {languageItems.length}
          </Title>
          {languageItems.map(({ key, icon, label }) => (
            <Row key={key}>
              {icon}
              <Value>{label}</Value>
            </Row>
          ))}
        </div>
      </DividerContainer>
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  height: '100%',
  boxSizing: 'border-box',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  justifyContent: 'space-between',
}));

const MainColumn = styled('div')({
  flexBasis: '65%',
  minWidth: 0,
});

const MainTitle = styled('div')(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  lineHeight: '28px',
  color: theme.palette.textPrimaryBlack,
  marginBottom: theme.spacing(3),
}));

const Section = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const TwoColumnSection = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(4),
}));

const Column = styled('div')({
  flex: 1,
  minWidth: 0,
});

const ColumnDivider = styled('div')(({ theme }) => ({
  width: 1,
  flexShrink: 0,
  alignSelf: 'stretch',
  backgroundColor: theme.palette.grey200,
}));

const SectionHeader = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
  paddingBottom: 8,
  marginBottom: 16,
  borderBottom: `2px solid ${theme.palette.primary.main}`,

  svg: {
    width: 18,
    height: 18,
  },
}));

const Bar = styled('div')(({ theme }) => ({
  width: 3,
  height: 14,
  borderRadius: 2,
  backgroundColor: theme.palette.primary.main,
}));

const OverviewGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
  gap: theme.spacing(1),
}));

const Tile = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
});

const TileLabel = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  fontSize: 11,
  fontWeight: 400,
  color: theme.palette.grey500,
  textAlign: 'center',

  svg: {
    width: 13,
    height: 13,
    flexShrink: 0,
  },
}));

const TileValue = styled('div')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
  textAlign: 'center',
}));

const InfoRow = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: '10px 0',
  borderBottom: `1px solid ${theme.palette.grey100}`,
  fontSize: 14,

  '&:last-of-type': {
    borderBottom: 'none',
  },
}));

const InfoLabel = styled('div')(({ theme }) => ({
  color: theme.palette.grey500,
  fontWeight: 400,
}));

const InfoValue = styled('div')(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
  textAlign: 'right',
}));

const FeaturesGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  rowGap: theme.spacing(3),
  columnGap: theme.spacing(3),
}));

const FeatureCell = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  paddingRight: theme.spacing(3),
}));

const FeatureCellColumn = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  width: '100%',
});

const FeatureList = styled('div')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.textPrimaryBlack,
}));

const Divider = styled('div')(({ theme }) => ({
  width: 1,
  height: `calc(100% - ${theme.spacing(6)})`,
  backgroundColor: theme.palette.grey[200],
}));

const Title = styled('div')(() => ({
  fontSize: 16,
  fontWeight: 600,
  lineHeight: '24px',
  marginBottom: 10,
}));

const Row = styled('div')({
  display: 'flex',
  gap: 13,
  alignItems: 'center',
  marginBottom: 14,

  svg: {
    width: 16,
    height: 16,
  },
});

const Value = styled('div')(() => ({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',

  b: {
    fontWeight: 500,
  },
}));

const DividerContainer = styled('div')(() => ({
  display: 'flex',
  gap: '16px',
  flexBasis: '35%',
}));

export default GameDetails;
